/**
 * Финансы и зарплаты — `01-BUSINESS-RULES.md` §4.
 *
 *   Доход месяца        = Выручка − Себестоимость − Аренда − Налог
 *   Доля сотрудника     = Доход месяца × (процент / 100)
 *   Зарплата сотрудника = max(доля, минимальная зарплата, 0) + премия
 *   Остаток владельца   = Доход месяца − Σ зарплат
 *
 * Минимальная зарплата общая для всех и задаётся в настройках. Она ЗАМЕНЯЕТ
 * долю, а не прибавляется к ней: пока процент даёт меньше гарантии, платится
 * гарантия; как только перерос — платится процент. Пересчёт идёт в течение
 * месяца сам собой, потому что доход считается по уже принятым заказам.
 *
 * Выручка и себестоимость считаются по ДАТЕ ПОЛУЧЕНИЯ заказа (`received`)
 * и не зависят от факта оплаты. Это финансовый план по объёму ПРИНЯТОЙ
 * работы, а не кассовый учёт: заказ, принятый в августе и оплаченный в
 * октябре, целиком относится к августу.
 *
 * Единственный статус, который на финансы влияет, — «Ожидание»: замороженный
 * врачом заказ не считается ни в выручке, ни в расходах, пока его не
 * разморозят. Работа по нему не идёт, материалы не тратятся, и денег за него
 * может не быть вовсе.
 */
import { monthKeyForOffset, monthKeyOf } from './dates.js';
import { shareApplies } from './periods.js';
/** Итоги одного месяца по ключу `YYYY-MM`. */
export function monthTotals(orders, monthKey, rentKop, 
/**
 * Налог месяца, копейки. Считается снаружи — по счетам, а не по заказам.
 *
 * Ноль по умолчанию: до появления налога расчёт работал без него, и все
 * прежние вызовы должны продолжать давать то же самое.
 */
taxKop = 0, 
/**
 * Прочие расходы месяца, копейки. Ноль по умолчанию: до их появления
 * расчёт работал без них, и прежние вызовы должны давать то же самое.
 */
otherKop = 0) {
    let revenueKop = 0;
    let expenseKop = 0;
    let orderCount = 0;
    for (const order of orders) {
        if (monthKeyOf(order.received) !== monthKey)
            continue;
        /*
          «Ожидание» ВЫПАДАЕТ из финансов целиком — и из выручки, и из расходов.
    
          Такой заказ заморожен врачом: работа не делается, материалы не тратятся,
          денег за него не будет, пока врач не разморозит. Учитывать его как
          принятую работу значило бы обещать выручку, которой может не случиться
          вовсе, — и завышать долю сотрудников от дохода месяца.
    
          Заказ не исчезает: разморозят — он вернётся в расчёт того месяца, в
          котором был принят, потому что финансы считаются по дате приёма.
        */
        /*
         * «Ожидание» и «Новый» ВЫПАДАЮТ из финансов целиком.
         *
         * Первый заморожен лабораторией, второй пришёл из кабинета врача и ещё
         * не проверен: цену в нём поставил справочник, а не человек. Считать по
         * ней выручку значит показать владельцу деньги, которых он не называл.
         */
        if (order.status === 'Ожидание' || order.status === 'Новый')
            continue;
        revenueKop += order.costKop;
        expenseKop += order.expenseKop;
        orderCount += 1;
    }
    return {
        monthKey,
        revenueKop,
        expenseKop,
        taxKop,
        otherKop,
        incomeKop: revenueKop - expenseKop - rentKop - taxKop - otherKop,
        /*
         * Прочие расходы в базу долей НЕ входят — как и налог (решение
         * владельца от 16.09.2026): процент техника считается от того, что
         * лаборатория заработала на работах, а покупка оборудования к его
         * работе отношения не имеет.
         */
        salaryBaseKop: revenueKop - expenseKop - rentKop,
        orderCount,
    };
}
/** Итоги месяца, смещённого на `offset` от сегодняшнего (0 — текущий, −1 — прошлый). */
export function monthTotalsByOffset(orders, today, offset, rentKop) {
    return monthTotals(orders, monthKeyForOffset(today, offset), rentKop);
}
/** Ряд помесячных итогов для графика: `span` последних месяцев, включая текущий. */
export function monthlyTrend(orders, today, span, rentKop) {
    const result = [];
    for (let i = span - 1; i >= 0; i -= 1) {
        result.push(monthTotalsByOffset(orders, today, -i, rentKop));
    }
    return result;
}
/** Изменение показателя к предыдущему месяцу, %. `null` — сравнивать не с чем. */
export function trendPercent(current, previous) {
    if (previous === 0)
        return null;
    return Math.round(((current - previous) / Math.abs(previous)) * 100);
}
/**
 * Зарплата одного сотрудника от базы месяца.
 *
 * База — выручка за вычетом себестоимости и аренды, БЕЗ налога:
 * см. `MonthTotals.salaryBaseKop`.
 *
 * Минимум — не пол, добавляемый к доле, а ЗАМЕНА ей: пока процент даёт меньше
 * гарантии, платится гарантия; как только перерос — платится процент целиком.
 * При минимуме 50 000 и доле 60 000 к выплате идёт 60 000, а не 110 000.
 *
 * Премия начисляется СВЕРХ этого и в минимум не входит: владелец назначает её
 * отдельно и за отдельное, а гарантия — условие работы, а не награда. Поэтому
 * же премия не уменьшается при убыточном месяце: она не должна превращаться
 * в удержание.
 *
 * `minSalaryKop` — обязательный параметр, а не значение по умолчанию: забытый
 * аргумент тихо вернул бы расчёт к прежнему поведению с отрицательной
 * зарплатой, и заметили бы это по платёжной ведомости, а не по ошибке сборки.
 */
export function salaryOf(employee, incomeKop, minSalaryKop, 
/**
 * Фактическая выплата, назначенная вручную за этот месяц.
 *
 * Ноль — ЗНАЧАЩЕЕ значение: месяц, за который не платили, приходится
 * записать именно нулём, и `undefined` здесь не годится.
 */
manual, 
/**
 * Месяц расчёта.
 *
 * Нужен одной проверке: доля от дохода действует с сентября 2026-го, а до
 * него зарплату считали сдельно и вносили руками. Необязателен нарочно —
 * без него берётся нынешний порядок, и старые вызовы считают как считали.
 */
monthKey) {
    /*
     * Прежний порядок: доли нет, и взяться ей неоткуда.
     *
     * Ставка у сотрудника записана одна — нынешняя. Приложи её к августу, и
     * выйдет число, которого никто не платил: в августе платили по таблице
     * учёта работ. Поэтому за такие месяцы доля, гарантия и премия обнуляются,
     * а итогом остаётся то, что внесено руками.
     */
    const доляДействует = monthKey === undefined || shareApplies(monthKey);
    /*
     * Доля округляется ДО ЦЕЛОГО РУБЛЯ.
     *
     * Процент от дохода почти никогда не даёт круглую сумму: девять процентов
     * от 1 103 560 ₽ — это 99 320,40 ₽. Копейки в зарплате не выплачиваются и
     * не пишутся в ведомости, а на экране мешают: рядом с ними прочие числа
     * читаются как приблизительные.
     *
     * Округление в БОЛЬШУЮ сторону: спор о копейках всегда решается в пользу
     * работника, и лаборатория теряет на этом меньше рубля в месяц.
     */
    const shareKop = доляДействует
        ? Math.ceil((incomeKop * employee.percent) / 100 / 100) * 100
        : 0;
    // Ноль в основании: при минимуме 0 и убыточном месяце доля всё равно не
    // должна уходить в минус — зарплата не бывает отрицательной.
    const baseKop = доляДействует ? Math.max(shareKop, minSalaryKop, 0) : 0;
    // Ручная премия принадлежит месяцу; постоянная премия старого API допустима
    // только в вызовах без календарного месяца. Исторические суммы не меняются.
    const премияKop = manual?.bonusKop ?? (monthKey === undefined ? employee.bonusKop : 0);
    const computedKop = baseKop + премияKop;
    /*
     * Назначенная сумма и аванс — РАЗНЫЕ вещи, и хранятся вместе.
     *
     * `null` у суммы означает «вручную не ставили»: итог считается по
     * проценту, но аванс из той же записи всё равно вычитается.
     */
    const назначеноKop = manual?.amountKop ?? null;
    const авансKop = Math.max(0, manual?.advanceKop ?? 0);
    const отпускныеKop = Math.max(0, manual?.vacationKop ?? 0);
    /*
     * Отпускные прибавляются и к расчёту, и к назначенной вручную сумме:
     * это деньги за дни, которых в работе месяца нет, и заменять ими долю
     * не за что.
     */
    const итогKop = (назначеноKop === null ? computedKop : назначеноKop) + отпускныеKop;
    return {
        employeeId: employee.id,
        name: employee.name,
        percent: employee.percent,
        bonusKop: премияKop,
        shareKop,
        minSalaryKop: доляДействует ? minSalaryKop : 0,
        baseKop,
        atMinimum: доляДействует && baseKop > shareKop,
        salaryKop: итогKop,
        vacationKop: отпускныеKop,
        advanceKop: авансKop,
        remainderKop: итогKop - авансKop,
        manual: назначеноKop !== null,
        computedKop: computedKop + отпускныеKop,
        manualNote: manual?.note ?? '',
        shareApplies: доляДействует,
    };
}
/** Распределение дохода месяца между сотрудниками и владельцем. */
export function payroll(employees, 
/** База для долей: выручка − себестоимость − аренда, без налога. */
salaryBaseKop, minSalaryKop, 
/**
 * Ручные суммы этого месяца по идентификатору сотрудника.
 *
 * Остаток владельца считается от ФАКТИЧЕСКИХ выплат: если за июль отдано
 * больше расчётного, эти деньги ушли из тех же средств, и показывать
 * владельцу нетронутый расчётный остаток значило бы обещать ему сумму,
 * которой уже нет.
 */
manual, 
/**
 * Доход владельца — после налога. По умолчанию равен базе долей.
 *
 * Разделены потому, что считаются от разного: доля техника — от
 * заработанного лабораторией, а то, что остаётся владельцу, — из тех же
 * денег уже за вычетом налога. Один параметр на обе роли занижал бы
 * зарплату ровно на долю налога.
 */
ownerIncomeKop = salaryBaseKop, 
/** Месяц ведомости: до сентября 2026 доли не было. См. `salaryOf`. */
monthKey) {
    const lines = employees.map((e) => salaryOf(e, salaryBaseKop, minSalaryKop, manual?.get(e.id), monthKey));
    const totalSalariesKop = lines.reduce((sum, line) => sum + line.salaryKop, 0);
    const totalAdvanceKop = lines.reduce((sum, line) => sum + line.advanceKop, 0);
    return {
        incomeKop: ownerIncomeKop,
        salaryBaseKop,
        lines,
        totalSalariesKop,
        totalAdvanceKop,
        totalRemainderKop: totalSalariesKop - totalAdvanceKop,
        /*
         * Владельцу остаётся доход за вычетом ВСЕЙ зарплаты, а не остатка:
         * аванс — те же деньги месяца, просто отданные раньше.
         */
        ownerRemainderKop: ownerIncomeKop - totalSalariesKop,
    };
}
/**
 * Налог УСН «доходы» за месяц.
 *
 * База — ВЫСТАВЛЕННЫЕ СЧЕТА, а не выручка по заказам. Это разные числа и
 * разные месяцы: работа, принятая в июне, попадает в счёт от 2 июля, и налог
 * за неё платится в третьем квартале, а не во втором. Считать налог от
 * выручки значило бы платить его раньше, чем возникла обязанность.
 *
 * Наличные в базу НЕ входят: по ним счета не выставляются, и в расчётный счёт
 * они не попадают. Это решение владельца, а не правило закона, — если однажды
 * наличные начнут проводиться, их придётся добавить сюда же.
 *
 * Округление до целого рубля вверх: налог платится рублями, и недоплата
 * из-за копеек обходится дороже переплаты.
 */
export function taxForMonth(invoices, monthKey, percent) {
    let baseKop = 0;
    for (const invoice of invoices) {
        if (monthKeyOf(invoice.date) === monthKey)
            baseKop += invoice.totalKop;
    }
    return { baseKop, taxKop: Math.ceil((baseKop * percent) / 100 / 100) * 100 };
}
//# sourceMappingURL=finance.js.map